/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import moment from 'moment';
import { CrudListType, CrudTemplate, ListColumns } from '../../../organisms/crud/form-types';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { GroupedMissingDrug, MissingDrug } from 'app/types/drug';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'app/components/atoms/button/button';
import { AiOutlineSwap, AiOutlineInfoCircle } from 'react-icons/ai';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { DateTime } from 'app/components/atoms/date-time/date-time';

/**
 * Grouped list columns.
 */

const listColumns = (translate: TFunction): ListColumns<GroupedMissingDrug> => [
  {
    title: translate('common.table.columns.code'),
    dataIndex: 'code',
    sortable: true,
    key: 'code',
    size: '120px'
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    caseInsensitive: true,
    sortable: true,
    key: 'name',
    size: '1fr'
  },
  {
    title: translate('common.table.columns.count'),
    dataIndex: 'count',
    sortable: true,
    key: 'count',
    size: '100px',
    render: (item: GroupedMissingDrug) => Number(item.count)
  },
  {
    title: translate('common.table.columns.lastOccurrence'),
    dataIndex: 'lastOccurrence',
    sortable: true,
    key: 'lastOccurrence',
    size: '200px',
    render: (item: GroupedMissingDrug) => <DateTime date={item.lastOccurrence} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'datePickerSearch',
    inline: true,
    name: 'fromDate',
    label: translate('common.labels.startDate'),
    disabledDate: date => date.isAfter(moment(), 'day')
  }
];

/**
 * Occurrences list columns.
 */

const occurrencesColumns = (translate: TFunction): ListColumns<MissingDrug> => [
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'user.name',
    key: 'user.name',
    size: '150px'
  },
  {
    title: translate('common.table.columns.email'),
    dataIndex: 'user.email',
    key: 'user.email',
    size: '200px'
  },
  {
    title: translate('common.table.columns.notes'),
    dataIndex: 'notes',
    key: 'notes',
    size: '1fr'
  },
  {
    title: translate('common.table.columns.deviceName'),
    dataIndex: 'device.deviceName',
    key: 'device.deviceName',
    size: '150px'
  },
  {
    title: translate('common.table.columns.operatingSystem'),
    dataIndex: 'device.operatingSystem',
    key: 'device.operatingSystem',
    size: '130px'
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '200px',
    render: (item: MissingDrug) => <DateTime date={item.createdAt} />
  }
];

/**
 * Grouped list.
 */

const list = (translate: TFunction): CrudListType<GroupedMissingDrug> => ({
  columns: listColumns(translate),
  filters: listFilters(translate),
  enableExport: true,
  route: appRoutes.dashboard.medical.missingDrugs,
  addButton: translate('common.actions.add'),
  canEdit: false,
  canRemove: false,
  key: ['missingDrugs'],
  normalizeFilters: (data: any) => {
    const { fromDate, ...filters } = data.filters ?? {};

    return pickBy(
      { ...data, filters: pickBy(filters, identity), fromDate: fromDate ? moment(fromDate).toISOString() : undefined },
      identity
    );
  },
  clickLine: (item: GroupedMissingDrug, { navigate }: any) => {
    const detailLink = resolveAppPath(appRoutes.dashboard.medical.missingDrugsDetail as any, { code: item.code });

    navigate(detailLink);
  },
  renderActions: (item: GroupedMissingDrug) => {
    const detailLink = resolveAppPath(appRoutes.dashboard.medical.missingDrugsDetail as any, { code: item.code });
    const convertLink = resolveAppPath(appRoutes.dashboard.medical.missingDrugsConvert as any, { code: item.code });

    return (
      <>
        <Link
          onClick={event => {
            event.stopPropagation();
          }}
          to={detailLink}
        >
          <Button icon={<AiOutlineInfoCircle />} size={'small'} />
        </Link>

        <Link
          onClick={event => {
            event.stopPropagation();
          }}
          to={convertLink}
        >
          <Button icon={<AiOutlineSwap />} size={'small'} />
        </Link>
      </>
    );
  },
  normalizeData: (result: any) => {
    return result;
  },
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  endpoint: apiEndpoints.missingDrugs
});

/**
 * Config.
 */

export function createMissingDrugsTemplate(translate: TFunction): CrudTemplate<GroupedMissingDrug> {
  return {
    list: list(translate)
  };
}

/**
 * Export `createMissingDrugOccurrencesTemplate`.
 */

export function createMissingDrugOccurrencesTemplate(translate: TFunction, code: string): CrudTemplate<MissingDrug> {
  return {
    list: {
      columns: occurrencesColumns(translate),
      route: appRoutes.dashboard.medical.missingDrugsDetail as any,
      canEdit: false,
      canRemove: true,
      key: ['missingDrugOccurrences', code],
      endpoint: apiEndpoints.missingDrugsOccurrences,
      normalizeFilters: (data: any) => {
        const { ...filters } = data.filters;
        const result: any = {
          filters: pickBy(filters, identity),
          order: data.order ?? []
        };

        return result;
      },
      pagination: {
        pageSize: 20,
        pageSizeOptions: [10, 20, 50, 100]
      }
    },
    remove: {
      endpoint: apiEndpoints.missingDrugsDelete,
      resolveParams: (_params: any, item?: MissingDrug) => ({ id: item?.code })
    }
  };
}
